import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import {
  Shield,
  Users,
  FileText,
  Settings,
  UserPlus,
  Loader2,
  Eye,
  Edit,
  Trash2,
  Crown,
  CheckCircle,
  XCircle,
  ArrowRight,
  LayoutDashboard,
  ClipboardCheck,
} from 'lucide-react';

const SuperAdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState([]);
  const [stats, setStats] = useState({ totalUsers: 0, totalCampaigns: 0, totalRaised: 0 });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin'
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch admin users from super-admin endpoint
      const adminsRes = await apiClient.get('/super-admin/admins');
      setAdminUsers(adminsRes.data.admins || []);

      // Fetch stats
      const analyticsRes = await apiClient.get('/platform/analytics/overview');
      setStats({
        totalUsers: analyticsRes.data.total_users || 0,
        totalCampaigns: analyticsRes.data.total_campaigns || 0,
        totalRaised: analyticsRes.data.total_raised || 0,
      });
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      alert('Please fill all fields');
      return;
    }
    setCreateLoading(true);
    try {
      await apiClient.post('/super-admin/create-admin', newAdmin);
      setShowCreateDialog(false);
      setNewAdmin({ name: '', email: '', password: '', role: 'admin' });
      fetchData();
      alert('Admin user created successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create admin');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteAdmin = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this admin?')) return;
    try {
      await apiClient.delete(`/super-admin/admin/${userId}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await apiClient.put(`/super-admin/admin/${userId}/role`, { role: newRole });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return <Badge className="bg-red-500/20 text-red-400"><Crown className="w-3 h-3 mr-1" /> Super Admin</Badge>;
      case 'platform_admin':
        return <Badge className="bg-purple-500/20 text-purple-400"><Settings className="w-3 h-3 mr-1" /> Platform Admin</Badge>;
      case 'admin':
        return <Badge className="bg-amber-500/20 text-amber-400"><Shield className="w-3 h-3 mr-1" /> Admin</Badge>;
      case 'reviewer':
        return <Badge className="bg-blue-500/20 text-blue-400"><ClipboardCheck className="w-3 h-3 mr-1" /> Reviewer</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">User</Badge>;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Super Admin Console</h1>
              <p className="text-muted-foreground">Full platform control and admin management</p>
            </div>
          </div>
        </div>

        {/* Stats Overview Cards - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card 
            className="bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border-emerald-500/30 cursor-pointer hover:border-emerald-400 transition-all"
            onClick={() => navigate('/platform-admin?tab=users')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-400 text-xs font-medium">Total Users</p>
                  <p className="text-2xl font-bold text-foreground">{stats.totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-emerald-400 opacity-50" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Click to manage →</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-teal-500/10 to-green-500/10 border-teal-500/30 cursor-pointer hover:border-teal-400 transition-all"
            onClick={() => navigate('/platform-admin?tab=campaigns')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-400 text-xs font-medium">Total Campaigns</p>
                  <p className="text-2xl font-bold text-foreground">{stats.totalCampaigns}</p>
                </div>
                <FileText className="w-8 h-8 text-teal-400 opacity-50" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Click to manage →</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30 cursor-pointer hover:border-amber-400 transition-all"
            onClick={() => navigate('/platform-admin?tab=overview')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-400 text-xs font-medium">Total Raised</p>
                  <p className="text-2xl font-bold text-foreground">${stats.totalRaised.toLocaleString()}</p>
                </div>
                <Shield className="w-8 h-8 text-amber-400 opacity-50" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Click for analytics →</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 cursor-pointer hover:border-purple-400 transition-all"
            onClick={() => navigate('/admin?tab=kyc')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-400 text-xs font-medium">Admin Users</p>
                  <p className="text-2xl font-bold text-foreground">{adminUsers.length}</p>
                </div>
                <Crown className="w-8 h-8 text-purple-400 opacity-50" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Manage below ↓</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link to="/platform-admin">
            <Card className="hover:border-teal-500/50 transition-all cursor-pointer group">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                      <LayoutDashboard className="w-5 h-5 text-teal-400" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Platform Admin Dashboard</CardTitle>
                      <CardDescription>SaaS metrics, users & campaigns</CardDescription>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-teal-400 transition-colors" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 text-sm">
                  <span className="text-muted-foreground">{stats.totalUsers} Users</span>
                  <span className="text-muted-foreground">{stats.totalCampaigns} Campaigns</span>
                  <span className="text-muted-foreground">${stats.totalRaised.toLocaleString()} Raised</span>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin">
            <Card className="hover:border-amber-500/50 transition-all cursor-pointer group">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <ClipboardCheck className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Admin Verification Panel</CardTitle>
                      <CardDescription>KYC reviews & campaign approvals</CardDescription>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-amber-400 transition-colors" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 text-sm">
                  <span className="text-muted-foreground">Review pending KYC</span>
                  <span className="text-muted-foreground">Approve campaigns</span>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Admin Users Management */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-400" />
                  Admin Users Management
                </CardTitle>
                <CardDescription>Create and manage admin access</CardDescription>
              </div>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
                    <UserPlus className="w-4 h-4 mr-2" /> Create Admin
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Admin User</DialogTitle>
                    <DialogDescription>
                      Add a new admin to help manage the platform
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={newAdmin.name}
                        onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                        placeholder="Admin Name"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newAdmin.email}
                        onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                        placeholder="admin@example.com"
                      />
                    </div>
                    <div>
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={newAdmin.password}
                        onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                        placeholder="••••••••"
                      />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin({ ...newAdmin, role: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="reviewer">Reviewer (KYC & Campaign Review)</SelectItem>
                          <SelectItem value="admin">Admin (Full Admin Access)</SelectItem>
                          <SelectItem value="platform_admin">Platform Admin (SaaS Dashboard)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateAdmin} disabled={createLoading}>
                      {createLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Create Admin
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {adminUsers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No admin users found</p>
              ) : (
                adminUsers.map((admin) => (
                  <div key={admin.id} className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                    <div className="flex items-center gap-4">
                      <img
                        src={admin.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(admin.name)}`}
                        alt={admin.name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{admin.name}</span>
                          {getRoleBadge(admin.role)}
                        </div>
                        <span className="text-sm text-muted-foreground">{admin.email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {admin.role !== 'super_admin' && (
                        <>
                          <Select 
                            value={admin.role} 
                            onValueChange={(v) => handleChangeRole(admin.id, v)}
                          >
                            <SelectTrigger className="w-[160px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="reviewer">Reviewer</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="platform_admin">Platform Admin</SelectItem>
                              <SelectItem value="user">Demote to User</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-400 hover:bg-red-500/10"
                            onClick={() => handleDeleteAdmin(admin.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {admin.role === 'super_admin' && (
                        <span className="text-sm text-muted-foreground italic">Cannot modify</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
